import { Action, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  AppointmentDetailDTO,
  AppointmentListDTO,
  AppointmentWindowListDTO,
  CompanyWindowListDTO,
  FieldIssue,
  Location,
  ServiceTitanApiCompany,
  ServiceTypeEnum,
  Technician,
  TradeEnum,
  ST,
  pick,
  ServiceTitanScheduledAppointment
} from '_lib/api/apiTypes.shim';

import { RootState } from '_lib/redux/store';
import { getTradeForNewCompany } from '_lib/utils/helpers';
import { ClearIssueItem, removeIssues } from '_lib/utils/validationErrorUtils';

import type { CustomerContext, SharedPhoneCallContext, SwitchProviderCompanyPayload } from '_lib/types/PhoneCall.types';

export interface HeldAppointment {
  appointment: AppointmentDetailDTO;
  appointmentWindow: AppointmentWindowListDTO; // TODO - set proper type
  companyWindow: CompanyWindowListDTO;
  tradeFromFilter?: TradeEnum;
  serviceTypeFromFilter?: ServiceTypeEnum;
}

export type HeldAppointmentContext = {
  heldAppointment?: HeldAppointment;
};

export interface PhoneCallContext extends SharedPhoneCallContext {
  appointmentTime?: string;
  location?: Location;
  cancelReason?: ST.JobCancelReason;
  overridePreviousAppointmentDate: boolean;
  overrideEmployeeId: boolean;
  assignedTech?: Technician;
  previousAppointment?: AppointmentListDTO;
  previousServiceTitanAppointment?: ServiceTitanScheduledAppointment;
  showRequestedAppointment: boolean;
}

export const toSharedPhoneCallContext = (phoneCallContext: PhoneCallContext): SharedPhoneCallContext => ({
  callOutcome: phoneCallContext.callOutcome,
  appointmentDateTime: phoneCallContext.appointmentDateTime,
  internalNotes: phoneCallContext.internalNotes,
  notes: phoneCallContext.notes,
  previousAppointmentDate: phoneCallContext.previousAppointmentDate,
  sendCompanyEmail: phoneCallContext.sendCompanyEmail,
  sendEscalationEmail: phoneCallContext.sendEscalationEmail,
  trade: phoneCallContext.trade,
  serviceType: phoneCallContext.serviceType
});

/** Subset of data that we can safely carry over when switching tenants, or upon a re-initialization from error */
const safeDataKeys = [
  'appointmentDateTime',
  'appointmentTime',
  'callOutcome',
  'internalNotes',
  'notes',
  'overridePreviousAppointmentDate',
  'overrideEmployeeId',
  'previousAppointmentDate',
  'sendCompanyEmail',
  'sendEscalationEmail',
  'serviceType',
  'showRequestedAppointment'
] as const satisfies (keyof PhoneCallContext)[];

const initialPhoneCallContext = (options?: Partial<PhoneCallContext>): PhoneCallContext => ({
  appointmentDateTime: options?.appointmentDateTime || null,
  appointmentTime: options?.appointmentTime,
  location: options?.location,
  callOutcome: options?.callOutcome,
  cancelReason: options?.cancelReason,
  internalNotes: options?.internalNotes || '',
  notes: options?.notes || '',
  overridePreviousAppointmentDate: !!options?.overridePreviousAppointmentDate,
  overrideEmployeeId: !!options?.overrideEmployeeId,
  assignedTech: options?.assignedTech,
  previousAppointment: options?.previousAppointment,
  previousAppointmentDate: options?.previousAppointmentDate || null,
  previousServiceTitanAppointment: options?.previousServiceTitanAppointment,
  sendCompanyEmail: !!options?.sendCompanyEmail,
  sendEscalationEmail: !!options?.sendEscalationEmail,
  serviceType: options?.serviceType || null,
  trade: options?.trade || null,
  showRequestedAppointment: !!options?.showRequestedAppointment
});

interface StApiPhoneCallSlice {
  heldAppointmentContext: HeldAppointmentContext;
  customerContext: CustomerContext | null;
  phoneCallContext: PhoneCallContext;
  phoneCallFieldIssues: FieldIssue[];
  phoneCallNonFieldErrors: string[];
  /* Control State */
  appointmentBookingPaneOpen: boolean;
}

const initialState = (phoneCallContext?: Partial<PhoneCallContext>): StApiPhoneCallSlice => ({
  heldAppointmentContext: { heldAppointment: undefined },
  customerContext: null,
  phoneCallContext: initialPhoneCallContext(phoneCallContext),
  phoneCallFieldIssues: [],
  phoneCallNonFieldErrors: [],
  appointmentBookingPaneOpen: false
});

type SetCustomerContextAction = PayloadAction<CustomerContext | null>;
type SetHeldAppointmentContext = PayloadAction<HeldAppointmentContext>;

export const stApiPhoneCallSlice = createSlice({
  name: 'stApiPhoneCallSlice',
  initialState: initialState(),
  reducers: {
    setAppointmentBookingPaneOpen(state, action: PayloadAction<boolean>) {
      state.appointmentBookingPaneOpen = action.payload;
    },
    setHeldAppointmentContext(state, action: SetHeldAppointmentContext) {
      state.heldAppointmentContext = action.payload;
    },
    setCustomerContext(state, action: SetCustomerContextAction) {
      state.customerContext = action.payload;
    },
    setCustomerContextLocations(state, action: PayloadAction<CustomerContext['locations']>) {
      if (state.customerContext) state.customerContext.locations = action.payload;
    },
    setPartialPhoneCallContext(
      state,
      action: PayloadAction<{
        updates: Partial<PhoneCallContext>;
        clearIssues?: ClearIssueItem[];
      }>
    ) {
      state.phoneCallContext = { ...state.phoneCallContext, ...action.payload.updates };
      if (state.phoneCallFieldIssues.length && action.payload.clearIssues?.length) {
        const updatedIssues = action.payload.clearIssues.reduce(
          (acc, item) => removeIssues({ ...(typeof item === 'string' ? { issuePath: item } : item), issues: acc }),
          state.phoneCallFieldIssues
        );
        if (updatedIssues.length !== state.phoneCallFieldIssues.length) state.phoneCallFieldIssues = updatedIssues;
      }
    },
    setPhoneCallFieldIssues(state, action: PayloadAction<FieldIssue[]>) {
      state.phoneCallFieldIssues = action.payload;
    },
    setPhoneCallNonFieldErrors(state, action: PayloadAction<string[]>) {
      state.phoneCallNonFieldErrors = action.payload;
    },
    initialize: (
      state,
      action: PayloadAction<{ company: ServiceTitanApiCompany; options?: { keepExistingCallData: boolean } }>
    ) => {
      const { company, options } = action.payload;

      const trade = getTradeForNewCompany(
        (options?.keepExistingCallData && state.phoneCallContext.trade) || null,
        company
      );

      return initialState({
        ...(options?.keepExistingCallData ? pick(state.phoneCallContext, safeDataKeys) : {}),
        trade
      });
    },
    switchDifferentTenantCompany: (state, action: PayloadAction<ServiceTitanApiCompany>) => {
      return {
        ...initialState({
          ...pick(state.phoneCallContext, safeDataKeys),
          trade: getTradeForNewCompany(state.phoneCallContext.trade, action.payload)
        }),
        phoneCallFieldIssues: state.phoneCallFieldIssues,
        phoneCallNonFieldErrors: state.phoneCallNonFieldErrors
      };
    },
    switchDifferentProviderCompany: (_state, action: PayloadAction<SwitchProviderCompanyPayload>) => {
      const { company, ...phoneCallContext } = action.payload || {};
      return initialState({
        ...phoneCallContext,
        trade: getTradeForNewCompany(phoneCallContext.trade, company)
      });
    },
    reset: () => initialState()
  },
  selectors: {
    selectCustomerContext: (state) => state.customerContext,
    selectHeldAppointmentContext: (state) => state.heldAppointmentContext,
    selectAppointmentBookingPaneOpen: (state) => state.appointmentBookingPaneOpen,
    selectPhoneCallContext: (state) => state.phoneCallContext,
    selectPhoneCallFieldIssues: (state) => state.phoneCallFieldIssues,
    selectPhoneCallNonFieldErrors: (state) => state.phoneCallNonFieldErrors
  }
});

export const {
  setCustomerContext,
  setCustomerContextLocations,
  setHeldAppointmentContext,
  setPartialPhoneCallContext,
  setPhoneCallFieldIssues,
  setPhoneCallNonFieldErrors,
  setAppointmentBookingPaneOpen,
  initialize,
  switchDifferentTenantCompany,
  switchDifferentProviderCompany,
  reset
} = stApiPhoneCallSlice.actions;

export const {
  selectCustomerContext,
  selectHeldAppointmentContext,
  selectAppointmentBookingPaneOpen,
  selectPhoneCallContext,
  selectPhoneCallFieldIssues,
  selectPhoneCallNonFieldErrors
} = stApiPhoneCallSlice.getSelectors((rootState: RootState) => rootState.stApiPhoneCallSlice);

/**
 * @returns whether or not the action is a SetCustomerContextAction AND the customer has changed.
 */
export const isCustomerChangePredicate = (
  action: Action,
  currentState: RootState,
  originalState: RootState
): action is SetCustomerContextAction =>
  setCustomerContext.match(action) &&
  currentState.stApiPhoneCallSlice.customerContext?.customer.providerId !==
    originalState.stApiPhoneCallSlice.customerContext?.customer.providerId;

/**
 * @returns whether or not the action is a SetHeldAppointmentContext and the heldAppointment has changed
 */
export const isHeldAppointmentChangePredicate = (
  action: Action,
  currentState: RootState,
  originalState: RootState
): action is SetHeldAppointmentContext =>
  setHeldAppointmentContext.match(action) &&
  currentState.stApiPhoneCallSlice.heldAppointmentContext.heldAppointment?.appointment.id !==
    originalState.stApiPhoneCallSlice.heldAppointmentContext.heldAppointment?.appointment.id;
